import React, { useRef, useState } from "react";
import { Box, Typography, IconButton, InputBase } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import { useTranslation } from "react-i18next";

/**
 * MenuItemCard - Single menu item preview card with inline editing
 *
 * @param {Object} item - { id, name, description, price, image }
 * @param {string} currencySymbol - Currency symbol from business location
 * @param {Function} onDelete - Delete item handler (removes card)
 * @param {Function} onItemUpdate - Called with (itemId, updatedFields) on local edits
 * @param {Function} onImageUpload - Called with (itemId, file) when user uploads an image
 */
const MenuItemCard = ({ item, currencySymbol = "$", onDelete, onItemUpdate, onImageUpload }) => {
  const { t } = useTranslation("dashboard");
  const [localName, setLocalName] = useState((item.name || "").toLowerCase());
  const [localDesc, setLocalDesc] = useState((item.description || "").toLowerCase());
  const [localPrice, setLocalPrice] = useState(item.price?.toFixed(2) || "0.00");
  const [localPreview, setLocalPreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const hasImage = (item.image && item.image.trim() !== "") || localPreview;
  const displayImage = localPreview || item.image;
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLocalPreview(previewUrl);
      onImageUpload?.(item.id, file);
    }
    e.target.value = "";
  };

  const handleDeleteImage = (e) => {
    e.stopPropagation();
    setLocalPreview(null);
    onItemUpdate?.(item.id, { image: "" });
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      onItemUpdate?.(item.id, {
        name: localName,
        description: localDesc,
        price: parseFloat(localPrice) || 0,
      });
    }
    setIsEditing(!isEditing);
  };

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        bgcolor: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Image area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 140,
          bgcolor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={displayImage}
            alt={localName}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <RestaurantMenuOutlinedIcon sx={{ fontSize: 32, color: "#d1d5db" }} />
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#9ca3af",
                textTransform: "lowercase",
              }}
            >
              {t("ocrItemCardNoImage")}
            </Typography>
          </Box>
        )}

        {/* Delete image button (top-right) */}
        {hasImage && (
          <IconButton
            onClick={handleDeleteImage}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              bgcolor: "#DC2626",
              color: "#fff",
              width: 28,
              height: 28,
              "&:hover": { bgcolor: "#B91C1C" },
            }}
          >
            <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {/* Camera + Generate AI buttons (only when no image) */}
        {!hasImage && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              display: "flex",
              gap: 0.5,
            }}
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 1.5,
                px: 1,
                py: 0.5,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: "#7C3AED", bgcolor: "#F3EEFF" },
              }}
            >
              <CameraAltOutlinedIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
            </Box>

            {/* Generate with AI — greyed out / disabled */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#d1d5db",
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                cursor: "not-allowed",
                opacity: 0.6,
              }}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: "#fff" }} />
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: "#fff",
                  textTransform: "lowercase",
                }}
              >
                {t("ocrItemCardGenerateAi")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Item details */}
      <Box sx={{ p: 1.5 }}>
        {/* Name */}
        {isEditing ? (
          <InputBase
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              width: "100%",
              mb: 0.25,
              borderBottom: "1px solid #7C3AED",
              "& .MuiInputBase-input": { p: 0, pb: 0.25, textTransform: "lowercase" },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {localName}
          </Typography>
        )}

        {/* Description */}
        {isEditing ? (
          <InputBase
            value={localDesc}
            onChange={(e) => setLocalDesc(e.target.value)}
            multiline
            maxRows={3}
            sx={{
              fontSize: "0.72rem",
              color: "#6b7280",
              textTransform: "lowercase",
              lineHeight: 1.4,
              width: "100%",
              mb: 1,
              borderBottom: "1px solid #7C3AED",
              "& .MuiInputBase-input": { p: 0, pb: 0.25, textTransform: "lowercase" },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#6b7280",
              textTransform: "lowercase",
              lineHeight: 1.4,
              mb: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {localDesc}
          </Typography>
        )}

        {/* Price row with edit/delete */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: "1px solid #e5e7eb",
              borderRadius: 1.5,
              px: 1,
              py: 0.25,
            }}
          >
            <Typography
              sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#6b7280" }}
            >
              {currencySymbol}
            </Typography>
            <InputBase
              value={localPrice}
              onChange={(e) => setLocalPrice(e.target.value)}
              readOnly={!isEditing}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "#111827",
                width: 60,
                "& .MuiInputBase-input": {
                  p: 0,
                  ...(isEditing && { borderBottom: "1px solid #7C3AED", pb: 0.25 }),
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0.25 }}>
            <IconButton
              onClick={handleToggleEdit}
              size="small"
              sx={{
                color: isEditing ? "#16a34a" : "#7C3AED",
                "&:hover": { bgcolor: isEditing ? "#DCFCE7" : "#F3EEFF" },
              }}
            >
              {isEditing ? (
                <CheckOutlinedIcon sx={{ fontSize: 18 }} />
              ) : (
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <IconButton
              onClick={() => onDelete?.(item.id)}
              size="small"
              sx={{ color: "#DC2626", "&:hover": { bgcolor: "#FEE2E2" } }}
            >
              <DeleteOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MenuItemCard;
